import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import SignatureModal from '../components/SignatureModal';
import SearchSelect from '../components/SearchSelect';
import useSignature from '../hooks/useSignature';
import { useBadges } from '../context/BadgesContext';
import { formatDateTime } from '../utils/date';
import { roleLabel } from '../utils/roles';

function Stat({ label, value, tone }) {
  const toneClass = {
    pending: 'text-status-rejected',
    done: 'text-status-approved',
  }[tone] || 'text-ink-primary';

  return (
    <div className="bg-surface-100 border rounded-md px-4 py-3">
      <div className={`text-2xl font-bold tracking-tight ${toneClass}`}>{value}</div>
      <div className="text-xs text-ink-muted mt-0.5">{label}</div>
    </div>
  );
}

// ── Visao do usuario comum ──

const PAGE_SIZE = 10;

function MyTrainings() {
  const { isAuditor } = useAuth();
  const [pending, setPending] = useState({ data: [], total: 0 });
  const [completed, setCompleted] = useState({ data: [], total: 0 });
  const [counts, setCounts] = useState({ pending: 0, completed: 0, awaiting_approval: 0 });
  const [pendingPage, setPendingPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirming, setConfirming] = useState(null);
  const signature = useSignature();
  const { refresh: refreshBadges } = useBadges();

  useEffect(() => { load(); }, [pendingPage, completedPage]);

  async function load() {
    setLoading(true);
    try {
      // Duas listas independentes, cada uma com sua propria pagina
      const [p, c] = await Promise.all([
        api.get('/training/me', { params: { status: 'pending', page: pendingPage, limit: PAGE_SIZE } }),
        api.get('/training/me', { params: { status: 'completed', page: completedPage, limit: PAGE_SIZE } }),
      ]);
      setPending(p.data);
      setCompleted(c.data);
      setCounts(p.data.counts);
    } catch { setError('Não foi possível carregar seus treinamentos'); }
    setLoading(false);
  }

  async function confirm(documentId) {
    setError(''); setSuccess('');
    setConfirming(documentId);
    try {
      await signature.run(
        async () => {
          await api.post(`/training/${documentId}/confirm`);
          setSuccess('Leitura confirmada.');
          await load();
          // Zera o alerta do menu na hora, sem esperar o timer
          refreshBadges();
        },
        {
          title: 'Confirmar leitura',
          description: 'Confirme sua identidade para registrar que você leu este documento. '
            + 'O registro fica na auditoria com seu nome, a versão lida e a data.',
        }
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao confirmar leitura');
    }
    setConfirming(null);
  }

  if (loading) return <div className="py-12 text-center text-ink-muted">Carregando...</div>;

  return (
    <div>
      {error && <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>}
      {success && <div className="p-3 rounded-md text-sm mb-4 bg-status-approved/10 text-status-approved border border-status-approved/20">{success}</div>}

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="grid grid-cols-2 gap-3 flex-1 max-w-[420px]">
          <Stat label="Pendentes de leitura" value={counts.pending} tone="pending" />
          <Stat label="Leituras confirmadas" value={counts.completed} tone="done" />
        </div>
        {/* Nova guia: o relatorio abre no leitor de PDF, onde da para imprimir ou salvar */}
        <button
          className="btn btn-secondary flex-shrink-0"
          onClick={() => window.open('/api/training/report/me', '_blank', 'noopener')}
        >
          Imprimir PDF
        </button>
      </div>

      <h2 className="text-base font-semibold mb-3">Pendentes</h2>
      {pending.data.length === 0 ? (
        <div className="bg-surface-100 border rounded-md p-6 text-center text-sm text-ink-muted mb-8">
          Nenhuma leitura pendente.
        </div>
      ) : (
        <div className="bg-surface-100 border rounded-md divide-y divide-subtle">
          {pending.data.map((t) => (
            <div key={t.document_id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="doc-code">{t.document_code}</span>
                  <span className="version-label">v{t.version_number}.{t.sub_version}</span>
                </div>
                <div className="text-sm font-medium mt-0.5 truncate">{t.document_name}</div>
                <div className="text-xs text-ink-muted">
                  {t.ultima_versao_lida
                    ? `Você leu a versão ${t.ultima_versao_lida}. O documento foi revisado e precisa de nova leitura.`
                    : `Designado em ${formatDateTime(t.assigned_at)}`}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 items-center">
                <Link className="btn btn-sm btn-secondary" to={`/documents/${t.document_id}`}>Ler documento</Link>
                {/* Perfil somente leitura nao confirma: o servidor recusaria
                    com 403, e oferecer o botao faria a pessoa tentar e achar
                    que o sistema esta com defeito. */}
                {isAuditor ? (
                  <span className="text-xs text-ink-muted italic"
                    title="Perfil Auditoria não confirma leitura. Esta designação é anterior à mudança de perfil.">
                    Não aplicável
                  </span>
                ) : (
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={confirming === t.document_id}
                    onClick={() => confirm(t.document_id)}
                  >
                    {confirming === t.document_id ? 'Confirmando...' : 'Confirmar leitura'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mb-8">
        <Pagination page={pendingPage} total={pending.total} limit={PAGE_SIZE} onPageChange={setPendingPage} />
      </div>

      <h2 className="text-base font-semibold mb-3">Concluídas</h2>
      {completed.data.length === 0 ? (
        <div className="bg-surface-100 border rounded-md p-6 text-center text-sm text-ink-muted">
          Nenhuma leitura confirmada ainda.
        </div>
      ) : (
        <div className="bg-surface-100 border rounded-md divide-y divide-subtle">
          {completed.data.map((t) => (
            <div key={t.document_id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="doc-code">{t.document_code}</span>
                  <span className="version-label">v{t.version_number}.{t.sub_version}</span>
                </div>
                <div className="text-sm font-medium mt-0.5 truncate">{t.document_name}</div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-xs text-status-approved">Lido em {formatDateTime(t.read_at)}</span>
                <Link className="btn btn-sm btn-secondary" to={`/documents/${t.document_id}`}>Abrir</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={completedPage} total={completed.total} limit={PAGE_SIZE} onPageChange={setCompletedPage} />

      {signature.pending && (
        <SignatureModal
          title={signature.pending.title}
          description={signature.pending.description}
          onClose={signature.cancel}
          onConfirmed={signature.confirmed}
        />
      )}

      {counts.awaiting_approval > 0 && (
        <div className="mt-6 p-3 rounded-md text-sm bg-surface-inset text-ink-secondary">
          {counts.awaiting_approval} documento(s) designado(s) a voce ainda nao tem versao aprovada.
          A leitura sera cobrada quando forem aprovados.
        </div>
      )}
    </div>
  );
}

// ── Visao de Administrador e Manutencao ──

function TrainingManagement() {
  const [overview, setOverview] = useState({ data: [], totals: {}, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // O que a pessoa digitou vs o que ja foi consultado
  const [searchAplicada, setSearchAplicada] = useState('');
  const requisicaoAtual = useRef(0);
  // Guardam o objeto {id, label}, nao so o id: a etiqueta do selecionado
  // precisa do nome, e ele não está mais na lista depois de uma nova busca.
  const [filtroDoc, setFiltroDoc] = useState(null);
  const [filtroUser, setFiltroUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docTotal, setDocTotal] = useState(0);
  const [docSearch, setDocSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState('');
  // Guarda os objetos, nao so os ids: com busca no servidor, quem foi marcado
  // pode sair do resultado e ainda assim precisa aparecer como selecionado.
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignError, setAssignError] = useState('');
  const [docRestrito, setDocRestrito] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!showAssign) return undefined;
    const timer = setTimeout(async () => {
      try {
        // Com documento escolhido, o servidor ja diz quem pode ve-lo. Assim
        // ninguem seleciona alguem que a designacao vai recusar depois.
        const { data } = await api.get('/training/eligible-users', {
          params: { search: userSearch || undefined, document_id: selectedDoc || undefined },
        });
        setUsers(data.data);
        setUserTotal(data.data.length);
        setDocRestrito(Boolean(data.is_restricted));
        // Documento trocado pode tornar invalida uma selecao anterior
        setSelectedUsers((prev) => prev.filter(
          (s) => !data.data.some((u) => u.id === s.id && u.can_view === false)
        ));
      } catch { /* mantem a lista anterior */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [userSearch, showAssign, selectedDoc]);

  // Busca no servidor enquanto se digita. O debounce evita uma requisicao
  // por tecla; 250ms e curto o bastante para parecer instantaneo.
  useEffect(() => {
    if (!showAssign) return undefined;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/training/assignable-documents', {
          params: { search: docSearch || undefined },
        });
        setDocuments(data.data);
        setDocTotal(data.total);
        // Se o documento escolhido saiu do filtro, limpa a selecao para nao
        // salvar algo que a pessoa não está mais vendo. Forma funcional para
        // nao precisar de selectedDoc nas dependencias do efeito.
        setSelectedDoc((cur) => (cur && !data.data.some((d) => d.id === cur) ? '' : cur));
      } catch { /* mantem a lista anterior */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [docSearch, showAssign]);

  // Debounce na busca livre: sem ele sai uma requisicao por tecla, e como as
  // respostas podem chegar fora de ordem, a de um termo mais curto sobrescreve
  // a do termo completo — a tela mostra resultado nao filtrado.
  useEffect(() => {
    const timer = setTimeout(() => setSearchAplicada(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { load(); }, [page, searchAplicada, filtroDoc, filtroUser]);

  async function load() {
    // Guarda contra resposta atrasada: so a requisicao mais recente escreve
    // na tela, mesmo que uma anterior demore mais para voltar.
    requisicaoAtual.current += 1;
    const minhaVez = requisicaoAtual.current;

    setLoading(true);
    try {
      const { data } = await api.get('/training/overview', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: searchAplicada || undefined,
          document_id: filtroDoc?.id || undefined,
          user_id: filtroUser?.id || undefined,
        },
      });
      if (minhaVez !== requisicaoAtual.current) return;
      setOverview(data);
    } catch {
      if (minhaVez === requisicaoAtual.current) setError('Não foi possível carregar o panorama de treinamentos');
    }
    if (minhaVez === requisicaoAtual.current) setLoading(false);
  }

  // Somente documentos e pessoas que tem designacao: filtro que retorna vazio
  // sem explicar por que e pior do que nao ter a opcao.
  async function buscarDocumentos(termo) {
    const { data } = await api.get('/training/filter-options', {
      params: { type: 'documents', search: termo || undefined },
    });
    return data.data.map((d) => ({ id: d.id, label: `${d.code} — ${d.name}` }));
  }

  async function buscarPessoas(termo) {
    const { data } = await api.get('/training/filter-options', {
      params: { type: 'users', search: termo || undefined },
    });
    return data.data.map((u) => ({ id: u.id, label: u.name, sublabel: u.email }));
  }

  function aplicarFiltro(setter) {
    return (opcao) => { setter(opcao); setPage(1); };
  }

  function limparFiltros() {
    setSearch(''); setSearchAplicada(''); setFiltroDoc(null); setFiltroUser(null); setPage(1);
  }

  async function openAssign() {
    setError(''); setAssignError('');
    setSelectedDoc(''); setSelectedUsers([]); setDocRestrito(false);
    setDocSearch(''); setUserSearch('');
    try {
      const [d, u] = await Promise.all([
        api.get('/training/assignable-documents'),
        api.get('/users/options'),
      ]);
      setDocuments(d.data.data);
      setDocTotal(d.data.total);
      setUsers(u.data.data);
      setUserTotal(u.data.total);
      setShowAssign(true);
    } catch {
      setError('Não foi possível carregar documentos ou usuários');
    }
  }

  async function submitAssign() {
    setError(''); setAssignError('');
    setSaving(true);
    try {
      const { data } = await api.post('/training/assignments', {
        document_id: selectedDoc,
        user_ids: selectedUsers.map((u) => u.id),
      });
      // Recusados por falta de acesso precisam aparecer: designar 3 de 5 em
      // silencio faria o gestor acreditar que cobriu todo mundo.
      if (data.sem_permissao?.length) {
        setError(`Designado para ${data.added} pessoa(s). Sem permissão de visualização, não designadas: `
          + data.sem_permissao.map((u) => u.name).join(', '));
      }
      setShowAssign(false);
      await load();
    } catch (err) {
      // Erro dentro do modal: ele continua aberto, e uma mensagem no fundo da
      // tela ficaria escondida atras da propria caixa.
      setAssignError(err.response?.data?.error || 'Erro ao designar leitura');
    }
    setSaving(false);
  }

  async function removeAssignment(assignmentId) {
    setError('');
    try {
      await api.delete(`/training/assignments/${assignmentId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover designação');
    }
  }

  function toggleUser(user) {
    setSelectedUsers((prev) => (prev.some((s) => s.id === user.id)
      ? prev.filter((s) => s.id !== user.id)
      : [...prev, user]));
  }

  if (loading) return <div className="py-12 text-center text-ink-muted">Carregando...</div>;

  const t = overview.totals;

  return (
    <div>
      {error && <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>}

      <div className="flex justify-between items-start mb-6 gap-4">
        {/* A quinta caixa so aparece quando ha o que mostrar: sem nenhuma
            designacao nao aplicavel, ela seria um zero sem contexto. */}
        <div className={`grid ${t.nao_aplicavel > 0 ? 'grid-cols-5 max-w-[760px]' : 'grid-cols-4 max-w-[620px]'} gap-3 flex-1`}>
          <Stat label="Documentos" value={t.documents ?? 0} />
          <Stat label="Designações" value={t.assignments ?? 0} />
          <Stat label="Lidos" value={t.read ?? 0} tone="done" />
          <Stat label="Pendentes" value={t.pending ?? 0} tone="pending" />
          {t.nao_aplicavel > 0 && <Stat label="Não aplicáveis" value={t.nao_aplicavel} />}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            className="btn btn-secondary"
            onClick={() => window.open('/api/training/report/overview', '_blank', 'noopener')}
          >
            Imprimir PDF
          </button>
          <button className="btn btn-primary" onClick={openAssign}>Designar leitura</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-2xs font-semibold text-ink-muted uppercase tracking-wider mb-1">Buscar</label>
          <input
            className="form-input w-[240px]"
            placeholder="Código ou nome do documento..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div className="w-[260px]">
          <label className="block text-2xs font-semibold text-ink-muted uppercase tracking-wider mb-1">Documento</label>
          <SearchSelect
            value={filtroDoc}
            onChange={aplicarFiltro(setFiltroDoc)}
            fetchOptions={buscarDocumentos}
            placeholder="Todos — digite para buscar..."
            emptyMessage="Nenhum documento com leitura designada."
            allLabel="Todos os documentos"
          />
        </div>

        <div className="w-[260px]">
          <label className="block text-2xs font-semibold text-ink-muted uppercase tracking-wider mb-1">Pessoa</label>
          <SearchSelect
            value={filtroUser}
            onChange={aplicarFiltro(setFiltroUser)}
            fetchOptions={buscarPessoas}
            placeholder="Todas — digite para buscar..."
            emptyMessage="Nenhuma pessoa com leitura designada."
            allLabel="Todas as pessoas"
          />
        </div>

        {(search || filtroDoc || filtroUser) && (
          <button className="btn btn-sm btn-secondary" onClick={limparFiltros}>Limpar filtros</button>
        )}
      </div>

      {overview.data.length === 0 ? (
        <div className="bg-surface-100 border rounded-md p-8 text-center text-sm text-ink-muted">
          {search || filtroDoc || filtroUser
            ? 'Nenhum resultado para os filtros aplicados.'
            : 'Nenhuma leitura obrigatória designada ainda.'}
        </div>
      ) : (
        <div className="bg-surface-100 border rounded-md divide-y divide-subtle">
          {overview.data.map((doc) => {
            const isOpen = expanded === doc.document_id;
            const complete = doc.total_pending === 0;
            return (
              <div key={doc.document_id}>
                <button
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-surface-inset"
                  onClick={() => setExpanded(isOpen ? null : doc.document_id)}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="doc-code">{doc.document_code}</span>
                      {doc.version && <span className="version-label">v{doc.version}</span>}
                    </div>
                    <div className="text-sm font-medium mt-0.5 truncate">{doc.document_name}</div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${complete ? 'text-status-approved' : 'text-status-rejected'}`}>
                        {doc.total_read} / {doc.total_assigned}
                      </div>
                      <div className="text-2xs text-ink-muted">
                        {complete ? 'todos leram' : `${doc.total_pending} pendente(s)`}
                        {/* O denominador inclui as nao aplicaveis, senao
                            "2 / 3" com 1 nao aplicavel pareceria erro de
                            conta. Explicitar quantas sao resolve. */}
                        {doc.total_nao_aplicavel > 0
                          && ` · ${doc.total_nao_aplicavel} n/a`}
                      </div>
                    </div>
                    <span className="text-ink-muted text-xs">{isOpen ? '▲' : '▼'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-3 bg-surface-inset">
                    {doc.assigned.map((a) => (
                      <div key={a.assignment_id} className="flex items-center justify-between py-2 border-b border-subtle last:border-0">
                        <div>
                          <div className="text-sm">{a.user_name}</div>
                          <div className="text-xs text-ink-muted">{a.user_email}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Nao aplicavel vem antes de pendente: a pessoa
                              passou a ter perfil somente leitura e nao
                              consegue confirmar. Mostrar "Pendente" cobraria
                              algo impossivel de cumprir. */}
                          {a.nao_aplicavel ? (
                            <span className="text-xs text-ink-muted italic"
                              title="Perfil Auditoria é somente leitura e não confirma leitura. A designação continua registrada como histórico.">
                              Não aplicável
                            </span>
                          ) : a.read_at ? (
                            <span className="text-xs text-status-approved">Lido em {formatDateTime(a.read_at)}</span>
                          ) : (
                            <span className="text-xs text-status-rejected">Pendente</span>
                          )}
                          <button
                            className="text-xs text-ink-muted hover:text-status-rejected"
                            onClick={() => removeAssignment(a.assignment_id)}
                          >
                            remover
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} total={overview.total} limit={PAGE_SIZE} onPageChange={setPage} />

      {showAssign && (
        <Modal title="Designar leitura obrigatória" onClose={() => setShowAssign(false)}>
          {assignError && (
            <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">
              {assignError}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Documento *</label>
            <input
              className="form-input mb-2"
              placeholder="Digite o código ou o nome do documento..."
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              autoFocus
            />
            {/* Lista clicavel em vez de <select size>: o listbox nativo tem
                comportamento irregular entre navegadores e nao combinava com
                o seletor de pessoas logo abaixo. */}
            <div className="border rounded-md divide-y divide-subtle max-h-[180px] overflow-y-auto">
              {documents.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-inset">
                  <input
                    type="radio"
                    name="training-document"
                    checked={selectedDoc === d.id}
                    onChange={() => setSelectedDoc(d.id)}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{d.name}</span>
                    <span className="block text-xs text-ink-muted">
                      {d.code} — v{d.version_number}.{d.sub_version}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="text-xs text-ink-muted mt-1">
              {documents.length === 0
                ? (docSearch
                  ? 'Nenhum documento aprovado corresponde a busca.'
                  : 'Nenhum documento aprovado. Só documentos aprovados podem ser designados.')
                : `${documents.length} documento(s) exibido(s)${docTotal > documents.length ? ` de ${docTotal}` : ''}. Refine a busca para encontrar mais rapido.`}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Quem deve ler * ({selectedUsers.length} selecionado(s))
            </label>

            {/* Explica de antemao por que alguns nomes aparecem bloqueados,
                e o que fazer a respeito */}
            {docRestrito && selectedDoc && (
              <div className="p-2.5 rounded-md text-xs mb-2 bg-surface-inset text-ink-secondary">
                Documento de <strong>acesso restrito</strong>. Para incluir esse usuário, adicione
                a pessoa a algum dos grupos de acesso do documento.
              </div>
            )}

            {/* Selecionados sempre a vista: a busca filtra a lista abaixo e
                esconderia quem ja foi marcado, sem a pessoa perceber */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleUser(u)}
                    title="Remover"
                    className="flex items-center gap-1.5 text-xs bg-brand/10 text-brand border border-brand/20 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-brand/20"
                  >
                    {u.name}
                    <span className="text-sm leading-none">&times;</span>
                  </button>
                ))}
              </div>
            )}

            <input
              className="form-input mb-2"
              placeholder="Digite o nome ou o e-mail da pessoa..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />

            <div className="border rounded-md divide-y divide-subtle max-h-[220px] overflow-y-auto">
              {users.map((u) => {
                // Sem acesso ao documento, a pessoa veria a pendencia e
                // levaria 403 ao abrir. Melhor bloquear aqui e dizer por que.
                const bloqueado = u.can_view === false;
                return (
                  <label
                    key={u.id}
                    title={bloqueado ? 'Sem permissão para visualizar este documento' : undefined}
                    className={`flex items-center gap-2 px-3 py-2 text-sm ${
                      bloqueado ? 'opacity-55 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-inset'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={bloqueado}
                      checked={selectedUsers.some((s) => s.id === u.id)}
                      onChange={() => !bloqueado && toggleUser(u)}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{u.name}</span>
                      <span className="block text-xs text-ink-muted truncate">
                        {bloqueado ? 'Sem permissão de visualização neste documento' : u.email}
                      </span>
                    </span>
                    <span className="text-xs text-ink-muted flex-shrink-0">{roleLabel(u.role)}</span>
                  </label>
                );
              })}
            </div>

            <div className="text-xs text-ink-muted mt-1">
              {users.length === 0
                ? 'Nenhum usuário ativo corresponde a busca.'
                : `${users.length} exibido(s)${userTotal > users.length ? ` de ${userTotal}` : ''}.`}
            </div>
          </div>

          {/* Botao desabilitado sem explicacao deixa a pessoa sem saber o que
              falta. O aviso diz qual condicao ainda nao foi satisfeita. */}
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-ink-muted">
              {!selectedDoc && selectedUsers.length === 0 && 'Selecione um documento e ao menos uma pessoa.'}
              {!selectedDoc && selectedUsers.length > 0 && 'Falta selecionar o documento na lista acima.'}
              {selectedDoc && selectedUsers.length === 0 && 'Falta marcar ao menos uma pessoa.'}
              {selectedDoc && selectedUsers.length > 0
                && `Pronto: 1 documento, ${selectedUsers.length} pessoa(s).`}
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button className="btn btn-secondary" onClick={() => setShowAssign(false)}>Cancelar</button>
              <button
                className="btn btn-primary"
                disabled={saving || !selectedDoc || selectedUsers.length === 0}
                onClick={submitAssign}
              >
                {saving ? 'Salvando...' : 'Designar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function Training() {
  const { isPrivileged } = useAuth();
  const [tab, setTab] = useState(isPrivileged ? 'gestao' : 'minhas');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold tracking-tight">Treinamento</h1>
      </div>

      {/* Quem gerencia tambem pode ser designado, entao ve as duas visoes */}
      {isPrivileged && (
        <div className="flex gap-1 mb-6 border-b">
          {[['gestao', 'Gestao'], ['minhas', 'Minhas leituras']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === key ? 'border-brand text-brand' : 'border-transparent text-ink-muted hover:text-ink-secondary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isPrivileged && tab === 'gestao' ? <TrainingManagement /> : <MyTrainings />}
    </div>
  );
}
